import React from 'react';
import ProfileAssistantRow from '../GeneralProfile/ProfileAssistantRow';
import ProfileUserAnswerRow from '../GeneralProfile/ProfileUserAnswerRow';
import { interpolateI18n } from '../utils/interpolateI18n';
import { PNEU_FIELD_STEPS, PNEU_GATE_STEP, PNEU_SECTION_COMPLETE_STEP } from './careerHistorySurveyPneuHelpers';
import { stepQuestionKey } from './careerHistorySurveySteps';
import type { CareerHistorySurveyFlow } from './useCareerHistorySurveyFlow';
import type { CareerHistorySurveyStep, PneuCheckupStep, WorkHistoryEntryStep } from './careerHistorySurveyTypes';
import { WORK_HISTORY_ENTRY_STEPS } from './careerHistorySurveyTypes';

function EntryQA({
  flow,
  hideSummaryRow,
  showFieldEdit,
  onEditField,
}: {
  flow: CareerHistorySurveyFlow;
  hideSummaryRow?: boolean;
  showFieldEdit?: boolean;
  onEditField?: (step: WorkHistoryEntryStep) => void;
}) {
  const undoVisible = (flag: boolean) => (showFieldEdit ? false : flag);

  return (
    <>
      {WORK_HISTORY_ENTRY_STEPS.map((s) => {
        // Dust duration is auto-derived from dates; only shown in entry summary.
        if (s === 'dustDuration') return null;
        if (!flow.isCommitted(s)) return null;
        if (s === 'entrySummary') {
          if (hideSummaryRow || showFieldEdit) return null;
          return (
            <React.Fragment key={s}>
              <ProfileAssistantRow>{flow.t('submitDocuments.careerHistorySurvey.steps.entrySummary')}</ProfileAssistantRow>
              <ProfileUserAnswerRow
                showUndo={undoVisible(flow.showUndoForStep(s))}
                onUndo={() => void flow.handleUndoStep(s)}
                undoLabel={flow.undoLabel}
              >
                {flow.userAnswerForStep(s)}
              </ProfileUserAnswerRow>
            </React.Fragment>
          );
        }
        return (
          <React.Fragment key={s}>
            <ProfileAssistantRow>{flow.t(stepQuestionKey(s))}</ProfileAssistantRow>
            <ProfileUserAnswerRow
              showUndo={undoVisible(flow.showUndoForStep(s))}
              onUndo={() => void flow.handleUndoStep(s)}
              undoLabel={flow.undoLabel}
              showEdit={showFieldEdit && s !== 'entrySummary' && s !== 'dustDuration'}
              onEdit={onEditField ? () => onEditField(s) : undefined}
              editLabel={flow.editLabel}
            >
              {flow.userAnswerForStep(s)}
            </ProfileUserAnswerRow>
          </React.Fragment>
        );
      })}
    </>
  );
}

function PneuQA({ flow }: { flow: CareerHistorySurveyFlow }) {
  return (
    <>
      {PNEU_FIELD_STEPS.map((s) => {
        if (!flow.isPneuCommitted(s)) return null;
        return (
          <React.Fragment key={s}>
            <ProfileAssistantRow>{flow.t(stepQuestionKey(s))}</ProfileAssistantRow>
            <ProfileUserAnswerRow
              showUndo={flow.showUndoForPneuStep(s)}
              onUndo={() => void flow.handleUndoPneuStep(s)}
              undoLabel={flow.undoLabel}
            >
              {flow.userAnswerForStep(s)}
            </ProfileUserAnswerRow>
          </React.Fragment>
        );
      })}
    </>
  );
}

export default function CareerHistorySurveyChatThread({ flow }: { flow: CareerHistorySurveyFlow }) {
  const { t } = flow;
  const yesLabel = t('submitDocuments.careerHistorySurvey.yes');
  const noLabel = t('submitDocuments.careerHistorySurvey.no');
  const ynLabel = (v: string) => (v === 'yes' ? yesLabel : v === 'no' ? noLabel : '');

  const entryHeader = (n: number) =>
    interpolateI18n(t('submitDocuments.careerHistorySurvey.entryHeader'), { index: String(n) });

  const activeInProgressNumber =
    flow.pendingGateEntryIndex !== null && flow.entryFieldsExpanded
      ? flow.pendingGateEntryIndex + 1
      : flow.completed.length + 1;

  const showWorkHistoryReviewMessage = flow.sectionWorkHistoryDone && flow.editIndex === null;

  return (
    <>
      <ProfileAssistantRow animateIn>{t('submitDocuments.careerHistorySurvey.workHistoryIntro')}</ProfileAssistantRow>

      {flow.isCommitted('workHistoryIntro') && (
        <ProfileAssistantRow animateIn>{t('submitDocuments.careerHistorySurvey.steps.workHistoryIntro')}</ProfileAssistantRow>
      )}

      {flow.completed.map((entry, i) => {
        if (flow.pendingReEdit && flow.pendingGateEntryIndex === i) {
          return null;
        }
        const isPendingCollapsed = flow.pendingGateEntryIndex === i && !flow.entryFieldsExpanded;
        return (
          <React.Fragment key={`wh-${entry.snapshot.id ?? i}`}>
            <ProfileAssistantRow>{entryHeader(i + 1)}</ProfileAssistantRow>
            {flow.editIndex === i ? (
              <EntryQA
                flow={flow}
                showFieldEdit={flow.showFieldEdit}
                onEditField={(s) => flow.beginEditField(s)}
              />
            ) : (
              <ProfileUserAnswerRow
                undoLabel={flow.undoLabel}
                showUndo={isPendingCollapsed && flow.showPendingSummaryUndo}
                onUndo={flow.handleUndoPendingSummary}
                showEdit={flow.showEditOnSummary(i)}
                onEdit={() => flow.handleEditSummary(i)}
                editLabel={flow.editLabel}
              >
                {entry.text}
              </ProfileUserAnswerRow>
            )}
          </React.Fragment>
        );
      })}

      {flow.recordGateCommitted && flow.editIndex === null && flow.step !== 'recordGate' && !flow.pendingReEdit && (
        <>
          <ProfileAssistantRow>{t('submitDocuments.careerHistorySurvey.steps.recordGate')}</ProfileAssistantRow>
          <ProfileUserAnswerRow
            undoLabel={flow.undoLabel}
            showUndo={flow.showUndoRecordGateNo}
            onUndo={() => void flow.handleUndoRecordGateNo()}
          >
            {ynLabel(flow.recordGateAnswer)}
          </ProfileUserAnswerRow>
        </>
      )}

      {showWorkHistoryReviewMessage && (
        <ProfileAssistantRow>
          {flow.completed.length > 0
            ? t('submitDocuments.careerHistorySurvey.workHistoryReview')
            : t('submitDocuments.careerHistorySurvey.noWorkHistoryDone')}
        </ProfileAssistantRow>
      )}

      {flow.inMemberFlow && flow.editIndex === null && (
        <>
          <ProfileAssistantRow>{entryHeader(activeInProgressNumber)}</ProfileAssistantRow>
          <EntryQA flow={flow} hideSummaryRow={(flow.step === 'entrySummary' || flow.step === 'dustDuration') && !flow.isCommitted('entrySummary')} />
        </>
      )}

      {flow.pneuGateCommitted && flow.editIndex === null && flow.step !== PNEU_GATE_STEP && (
        <>
          <ProfileAssistantRow>{t('submitDocuments.careerHistorySurvey.steps.pneuGate')}</ProfileAssistantRow>
          <ProfileUserAnswerRow
            undoLabel={flow.undoLabel}
            showUndo={flow.showUndoPneuGate}
            onUndo={() => void flow.handleUndoPneuGate()}
          >
            {ynLabel(flow.pneuDraft.gateAnswer)}
          </ProfileUserAnswerRow>
        </>
      )}

      {flow.sectionWorkHistoryDone && flow.editIndex === null && <PneuQA flow={flow} />}

      {flow.step === 'recordGate' && !flow.recordGateCommitted && (
        <div ref={flow.currentQuestionRef}>
          <ProfileAssistantRow animateIn>{t('submitDocuments.careerHistorySurvey.steps.recordGate')}</ProfileAssistantRow>
        </div>
      )}

      {flow.inMemberFlow &&
        flow.editIndex === null &&
        !flow.isCommitted(flow.step) &&
        flow.step !== 'recordGate' &&
        flow.step !== 'entrySummary' &&
        flow.step !== 'dustDuration' && (
          <div ref={flow.currentQuestionRef}>
            <ProfileAssistantRow animateIn>{t(stepQuestionKey(flow.step as CareerHistorySurveyStep))}</ProfileAssistantRow>
          </div>
        )}

      {(flow.step === 'entrySummary' || flow.step === 'dustDuration') &&
        !flow.isCommitted('entrySummary') &&
        flow.editIndex === null && (
        <div ref={flow.currentQuestionRef}>
          <ProfileAssistantRow animateIn>{t('submitDocuments.careerHistorySurvey.steps.entrySummary')}</ProfileAssistantRow>
        </div>
      )}

      {flow.inPneuFlow && !flow.isPneuCommitted(flow.step as PneuCheckupStep) && flow.step !== PNEU_SECTION_COMPLETE_STEP && (
        <div ref={flow.currentQuestionRef}>
          <ProfileAssistantRow animateIn>{t(stepQuestionKey(flow.step as CareerHistorySurveyStep))}</ProfileAssistantRow>
        </div>
      )}

      {flow.reviewMode && flow.editIndex === null && !flow.sectionWorkHistoryDone && (
        <div ref={flow.currentQuestionRef}>
          <ProfileAssistantRow animateIn>
            {flow.completed.length > 0
              ? t('submitDocuments.careerHistorySurvey.workHistoryReview')
              : t('submitDocuments.careerHistorySurvey.noWorkHistoryDone')}
          </ProfileAssistantRow>
        </div>
      )}
    </>
  );
}
